"use client";

import { useState } from "react";
import Image from "next/image";
import { ImageOff } from "lucide-react";

import { cn } from "@/lib/utils";

export interface DesignSearchResultImageProps {
  src: string;
  alt: string;
  /**
   * Frame aspect ratio (width / height). Default `1` matches the square result grid in fiq-frontend
   * (`aspect-square` + DesignSearchResultCard).
   */
  ratio?: number;
  className?: string;
}

/**
 * Thumbnail frame for CDN design-search images — same layout intent as
 * `fiq-frontend` `DesignSearchResultCard`: bounded box, neutral background, image scaled with
 * `object-contain` so varying patent drawing proportions are letterboxed instead of cropped.
 *
 * Shows a placeholder icon if the CDN image fails to load.
 */
export function DesignSearchResultImage({
  src,
  alt,
  ratio = 1,
  className,
}: DesignSearchResultImageProps) {
  const [failed, setFailed] = useState(false);

  return (
    <div
      className={cn(
        "relative w-full min-w-0 overflow-hidden bg-neutral-gray",
        className
      )}
      style={{ aspectRatio: ratio }}
    >
      {failed ? (
        <div className="flex h-full w-full flex-col items-center justify-center gap-3 bg-gradient-to-br from-slate-100 to-slate-200 dark:from-slate-800 dark:to-slate-900">
          <div className="flex h-14 w-14 items-center justify-center rounded-2xl bg-white/80 shadow-sm ring-1 ring-black/5 dark:bg-white/10 dark:ring-white/10">
            <ImageOff className="h-7 w-7 text-slate-400" strokeWidth={1.5} />
          </div>
          <div className="flex items-center gap-1.5">
            <div className="h-px w-6 bg-slate-300 dark:bg-slate-600" />
            <span className="text-[11px] font-medium tracking-wide text-slate-400">UNAVAILABLE</span>
            <div className="h-px w-6 bg-slate-300 dark:bg-slate-600" />
          </div>
        </div>
      ) : (
        <Image
          src={src}
          alt={alt}
          fill
          className="object-contain p-1"
          sizes="(max-width: 640px) 45vw, (max-width: 1024px) 25vw, 20vw"
          onError={() => setFailed(true)}
        />
      )}
    </div>
  );
}
